import React, { useState, useEffect } from "react";
import {
  Form,
  Button,
  Card,
  Row,
  Col,
  ToggleButtonGroup,
  ToggleButton,
} from "react-bootstrap";

export interface ManualVitalsData {
  heartRate?: number;
  respiratoryRate?: number;
  temperature?: number;
  weight?: number;
  notes?: string;
  timestamp: string;
  deviceId: string;
  sessionId?: string;
  patientId?: string | number;
}

interface Props {
  onSubmit: (vitals: ManualVitalsData) => void;
  defaultValues?: Partial<ManualVitalsData>;
  disabled?: boolean;
  deviceId: string;
  sessionId?: string;
  patientId?: string | number;
}

const ManualVitalsForm: React.FC<Props> = ({
  onSubmit,
  defaultValues = {},
  disabled = false,
  deviceId,
  sessionId,
  patientId,
}) => {
  const [heartRate, setHeartRate] = useState<string>(
    defaultValues.heartRate?.toString() || ""
  );
  const [respiratoryRate, setRespiratoryRate] = useState<string>(
    defaultValues.respiratoryRate?.toString() || ""
  );
  const [temperature, setTemperature] = useState<string>(
    defaultValues.temperature?.toString() || ""
  );
  const [weight, setWeight] = useState<string>(
    defaultValues.weight?.toString() || ""
  );
  const [notes, setNotes] = useState<string>(defaultValues.notes || "");

  const [unit, setUnit] = useState<"C" | "F">("C");

  useEffect(() => {
    const savedUnit = localStorage.getItem("temperatureUnit");
    if (savedUnit === "F" || savedUnit === "C") {
      setUnit(savedUnit);
    }
  }, []);

  const handleUnitChange = (newUnit: "C" | "F") => {
    if (temperature) {
      const tempVal = parseFloat(temperature);
      if (!isNaN(tempVal)) {
        const converted =
          newUnit === "C"
            ? (((tempVal - 32) * 5) / 9).toFixed(1)
            : ((tempVal * 9) / 5 + 32).toFixed(1);
        setTemperature(converted);
      }
    }
    setUnit(newUnit);
    localStorage.setItem("temperatureUnit", newUnit);
  };

  const getCurrentTimestamp = (): string => {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
      now.getDate()
    )} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data: ManualVitalsData = {
      deviceId,
      timestamp: getCurrentTimestamp(),
    };
    if (heartRate) data.heartRate = Number(heartRate);
    if (respiratoryRate) data.respiratoryRate = Number(respiratoryRate);
    if (temperature) {
      const tempVal = parseFloat(temperature);
      data.temperature = unit === "F" ? ((tempVal - 32) * 5) / 9 : tempVal;
    }
    if (weight) data.weight = Number(weight);
    if (notes) data.notes = notes;
    if (sessionId) data.sessionId = sessionId;
    if (patientId) data.patientId = patientId;

    onSubmit(data);
  };

  return (
    <Card className="p-3">
      <h5>Manual Vitals Entry</h5>
      <Form onSubmit={handleSubmit}>
        <Row className="mb-3">
          <Col>
            <Form.Label>Heart Rate (bpm)</Form.Label>
            <Form.Control
              type="number"
              value={heartRate}
              onChange={(e) => setHeartRate(e.target.value)}
              disabled={disabled}
              placeholder="Optional"
            />
          </Col>
          <Col>
            <Form.Label>Respiratory Rate (bpm)</Form.Label>
            <Form.Control
              type="number"
              value={respiratoryRate}
              onChange={(e) => setRespiratoryRate(e.target.value)}
              disabled={disabled}
              placeholder="Optional"
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <Form.Label>Temperature ({unit === "F" ? "°F" : "°C"})</Form.Label>
            <Form.Control
              type="number"
              step="0.1"
              value={temperature}
              onChange={(e) => setTemperature(e.target.value)}
              disabled={disabled}
              placeholder="Optional"
            />
            <ToggleButtonGroup
              className="mt-2"
              type="radio"
              name="temperatureUnit"
              value={unit}
              onChange={handleUnitChange}
            >
              <ToggleButton
                id="celsius-btn"
                variant="outline-primary"
                value="C"
                disabled={disabled}
              >
                °C
              </ToggleButton>
              <ToggleButton
                id="fahrenheit-btn"
                variant="outline-primary"
                value="F"
                disabled={disabled}
              >
                °F
              </ToggleButton>
            </ToggleButtonGroup>
          </Col>
          <Col>
            <Form.Label>Weight (kg)</Form.Label>
            <Form.Control
              type="number"
              step="0.1"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              disabled={disabled}
              placeholder="Optional"
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col>
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={disabled}
              placeholder="Add notes about the patient: Are they sleeping? Active? Jumpy? etc."
            />
          </Col>
        </Row>
        <Button type="submit" variant="primary" disabled={disabled}>
          Save Vitals
        </Button>
      </Form>
    </Card>
  );
};

export default ManualVitalsForm;